import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
  CardBodyComponent,
  CardComponent,
  StateMessageComponent,
} from '@groundworks/ngx-groundworks-ui';
import {
  AppointmentHistoryItem,
  mergeAndSortAppointments,
} from '../../models/appointment-history-item.model';
import {
  AttachmentInfo,
  CustomerOpportunitiesInfo,
  DeactivateOpportunityRequest,
  OpportunityInfo,
  QuoteInfo,
  SaleApprovalChecklistItems,
  SaleInfo,
  SalesTouchpointInfo,
  UpdateQuoteRequest,
} from '../../sdk/sdk';
import { ConfigService } from '../../config.service';
import { AttachmentService } from '../../services/attachment.service';
import { ModalService } from '../../services/modal.service';
import { OpportunityService } from '../../services/opportunity.service';
import { QuoteService } from '../../services/quote.service';
import { SalesTouchpointService } from '../../services/sales-touchpoint.service';
import { AttachmentsComponent } from '../attachments/attachments.component';
import { CustomerOpportunitiesCardComponent } from '../customer-opportunities-card/customer-opportunities-card.component';
import { PaymentsComponent } from '../payments/payments.component';
import { PendingSaleActivityComponent } from '../pending-sale-activity/pending-sale-activity.component';
import { QuoteViewerComponent } from '../quote-viewer/quote-viewer.component';
import { SalesTouchpointsComponent } from '../sales-touchpoints/sales-touchpoints.component';
import { SelectedOpportunityComponent } from '../selected-opportunity/selected-opportunity.component';

@Component({
  selector: 'app-pending-sale',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SalesTouchpointsComponent,
    PaymentsComponent,
    PendingSaleActivityComponent,
    SelectedOpportunityComponent,
    CustomerOpportunitiesCardComponent,
    QuoteViewerComponent,
    AttachmentsComponent,
    CardComponent,
    CardBodyComponent,
    StateMessageComponent,
  ],
  templateUrl: './pending-sale.component.html',
})
export class PendingSaleComponent implements OnInit {
  private readonly salesTouchpointService = inject(SalesTouchpointService);
  private readonly modalService = inject(ModalService);
  private readonly configService = inject(ConfigService);
  readonly customerCardEnabled = this.configService.enableService;
  readonly paymentsEnabled = this.configService.enablePayments;
  readonly opportunityId = signal<string | null>(null);
  readonly opportunity = signal<OpportunityInfo | undefined>(undefined);
  readonly opportunityLoading = signal(false);
  readonly opportunityError = signal(false);
  readonly customerOpportunities = signal<CustomerOpportunitiesInfo | undefined>(undefined);
  readonly customerOpportunitiesLoading = signal(false);
  readonly customerOpportunitiesError = signal(false);
  readonly deactivatingOpportunity = signal(false);
  readonly appointments = signal<AppointmentHistoryItem[]>([]);
  readonly firstAppointment = computed(() => {
    const apps = this.appointments();
    return apps.length > 0 ? apps[0] : null;
  });
  readonly touchPoints = signal<SalesTouchpointInfo[]>([]);
  readonly touchPointsLoading = signal(false);
  readonly touchPointsError = signal(false);
  readonly attachments = signal<AttachmentInfo[]>([]);
  readonly attachmentsLoading = signal(false);
  readonly attachmentsError = signal(false);
  readonly quotes = signal<QuoteInfo[]>([]);
  readonly removeReason = signal<DeactivateOpportunityRequest | null>(null);

  readonly activeQuote = computed(() => {
    const quotesValue = this.quotes();
    return quotesValue.length > 0 ? quotesValue[0] : null;
  });

  readonly currentRoute = signal<'pending-sale' | 'quote-review'>('pending-sale');
  readonly isQuoteReview = computed(() => this.currentRoute() === 'quote-review');
  readonly isPendingSale = computed(() => this.currentRoute() === 'pending-sale');
  readonly quotesState = computed(() => {
    if (this.opportunityLoading()) {
      return 'loading';
    }
    if (this.opportunityError()) {
      return 'error';
    }
    const quotesValue = this.quotes();
    if (!quotesValue || quotesValue.length === 0) {
      return 'no-matches';
    }
    return 'matches';
  });

  getQuotesState(): string {
    return this.quotesState();
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly opportunityService = inject(OpportunityService);
  private readonly route = inject(ActivatedRoute);
  private readonly attachmentsService = inject(AttachmentService);
  private readonly quoteService = inject(QuoteService);

  ngOnInit() {
    this.detectRoute();
    this.subscribeToRouteChanges();
  }

  /**
   * Subscribes to route query parameter changes to load the appropriate opportunity.
   * When opportunityId changes, triggers opportunity data loading.
   */
  private subscribeToRouteChanges() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.opportunityId.set(params['opportunityId'] ?? null);

      const idValue = this.opportunityId();
      if (idValue) {
        this.loadOpportunity(idValue);
        this.loadTouchPoints(idValue);
      }
    });
  }

  /**
   * Detects the current route and updates the currentRoute signal.
   * Checks the route path to determine if accessed via '/pending-sale' or '/quote-review'.
   */
  private detectRoute(): void {
    const routePath = this.route.snapshot.routeConfig?.path;
    if (routePath === 'quote-review') {
      this.currentRoute.set('quote-review');
    } else {
      this.currentRoute.set('pending-sale');
    }
  }

  /**
   * Loads opportunity data from the API.
   * @param opportunityId - The ID of the opportunity to load
   */
  private loadOpportunity(opportunityId: string): void {
    this.opportunityLoading.set(true);
    this.opportunityError.set(false);
    this.opportunityService.getOpportunity(opportunityId).subscribe({
      next: (opportunity) => {
        this.initializeOpportunityState(opportunity);
        this.opportunityLoading.set(false);
        this.opportunityError.set(false);
      },
      error: (error) => {
        console.error('Error loading opportunity:', error);
        this.opportunityLoading.set(false);
        this.opportunityError.set(true);
      },
    });
  }

  private loadTouchPoints(opportunityId: string): void {
    this.touchPointsLoading.set(true);
    this.touchPointsError.set(false);

    this.salesTouchpointService.getSalesTouchpoints(opportunityId).subscribe({
      next: (touchpoints) => {
        this.touchPoints.set(touchpoints);
        this.touchPointsLoading.set(false);
        this.touchPointsError.set(false);
      },
      error: (error) => {
        console.error('Error loading touchpoints:', error);
        this.touchPointsLoading.set(false);
        this.touchPointsError.set(true);
      },
    });
  }

  /**
   * Initializes component state after opportunity data is loaded.
   * Sets appointments, quotes, and triggers attachment loading if applicable.
   * @param opportunity - The loaded opportunity data
   */
  private initializeOpportunityState(opportunity: OpportunityInfo) {
    this.opportunity.set(opportunity);

    this.appointments.set(
      mergeAndSortAppointments(
        opportunity.appointments ?? [],
        opportunity.serviceAppointments ?? [],
      ),
    );
    this.quotes.set(opportunity.quotes ?? []);

    const oppValue = this.opportunity();

    this.loadOpportunityAttachments(oppValue?.id ?? '');

    if (this.customerCardEnabled) {
      this.loadCustomerOpportunities(opportunity.customerId);
    }
  }

  private loadCustomerOpportunities(customerId: number | undefined) {
    if (customerId == null) {
      this.customerOpportunitiesLoading.set(false);
      return;
    }

    this.customerOpportunitiesLoading.set(true);
    this.customerOpportunitiesError.set(false);

    this.opportunityService
      .getCustomerOpportunities(customerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (customerOpportunities) => {
          this.customerOpportunities.set(customerOpportunities);
          this.customerOpportunitiesLoading.set(false);
          this.customerOpportunitiesError.set(false);
        },
        error: (error) => {
          console.error('Error loading customer opportunities:', error);
          this.customerOpportunitiesLoading.set(false);
          this.customerOpportunitiesError.set(true);
        },
      });
  }

  private loadOpportunityAttachments(opportunityId: string) {
    this.attachmentsLoading.set(true);
    this.attachmentsError.set(false);

    this.attachmentsService
      .getAttachmentsByOpportunityId(opportunityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (attachments) => {
          this.attachments.set(attachments);
          this.attachmentsLoading.set(false);
          this.attachmentsError.set(false);
        },
        error: (error) => {
          console.error('Error loading attachments:', error);
          this.attachmentsLoading.set(false);
          this.attachmentsError.set(true);
        },
      });
  }

  /**
   * Handles changes to quote checklist items from child component.
   * Updates the quote on the server and syncs local state on success.
   * @param items - The updated checklist items
   */
  onQuoteCompletedItemsChange(items: SaleApprovalChecklistItems): void {
    const activeQuoteValue = this.activeQuote();
    if (!activeQuoteValue) {
      console.error('Cannot update quote: No quote available');
      return;
    }

    const updateRequest = new UpdateQuoteRequest({
      completedSaleApprovalItems: items,
    });

    this.quoteService
      .updateQuote(activeQuoteValue.id, updateRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedQuote) => {
          this.syncQuoteState(updatedQuote);
        },
        error: (error) => {
          console.error('Error updating quote:', error);
        },
      });
  }

  /**
   * Handles sale creation event from child component.
   * Updates the quote with the sale information and changes opportunity phase to "Sold".
   */
  onSaleCreated(event: { quoteId: string; sale: SaleInfo }): void {
    // Update the quote with the sale
    const quotesValue = this.quotes();
    const updatedQuotes = quotesValue.map((quote) => {
      if (quote.id === event.quoteId) {
        // Create a new QuoteInfo with the sale
        return QuoteInfo.fromJS({ ...quote.toJSON(), sale: event.sale });
      }
      return quote;
    });
    this.quotes.set(updatedQuotes);

    // Update the opportunity phase to "Sold"
    const currentOpportunity = this.opportunity();
    if (currentOpportunity) {
      const updatedOpportunity = OpportunityInfo.fromJS({
        ...currentOpportunity.toJSON(),
        phase: {
          ...currentOpportunity.phase,
          name: 'Sold',
        },
      });
      this.opportunity.set(updatedOpportunity);
    }
  }

  /**
   * Handles appointment creation event from child component.
   * Adds the new appointment to the top of the appointments list.
   */
  onAppointmentCreated(appointment: AppointmentHistoryItem): void {
    this.appointments.update((current) => [appointment, ...current]);
  }

  /**
   * Handles the remove from follow-up action.
   * Deactivates the opportunity and shows appropriate success or error feedback.
   */
  onRemoveFromFollowUp(request: DeactivateOpportunityRequest): void {
    const oppId = this.opportunityId();
    if (!oppId) {
      console.error('Cannot deactivate opportunity: No opportunity ID available');
      return;
    }

    this.removeReason.set(request);
    this.deactivatingOpportunity.set(true);

    this.opportunityService
      .deactivateOpportunity(oppId, this.removeReason()!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (_result) => {
          this.deactivatingOpportunity.set(false);
          this.modalService.openSuccessModal(
            'Removed from Follow-Up',
            'The opportunity has been successfully removed from follow-up.',
          );
        },
        error: (error) => {
          console.error('PendingSaleComponent: Error deactivating opportunity:', error);
          this.deactivatingOpportunity.set(false);

          let errorMessage: string;

          if (error?.status === 403) {
            errorMessage = 'You do not have permission to perform this action';
          } else if (error?.status === 404) {
            errorMessage = error?.detail || 'The opportunity could not be found';
          } else if (error?.status === 500) {
            errorMessage = error?.detail || 'Internal server error occurred';
          } else {
            errorMessage =
              error?.detail ||
              error?.message ||
              'An unexpected error occurred while removing from follow-up. Please try again.';
          }

          this.modalService.openErrorModal('Error Removing from Follow-Up', errorMessage);
        },
      });
  }

  /**
   * Synchronizes local quote state with server response after update.
   * Updates the quotes array immutably, which automatically updates the computed activeQuote.
   * @param updatedQuote - The quote data returned from the server
   */
  private syncQuoteState(updatedQuote: QuoteInfo): void {
    const activeQuoteValue = this.activeQuote();
    if (!activeQuoteValue) {
      console.warn('Attempted to update quote state but no active quote exists');
      return;
    }

    this.quotes.update((currentQuotes) =>
      currentQuotes.map((quote) =>
        quote.id === updatedQuote.id
          ? new QuoteInfo({
              ...quote,
              completedSaleApprovalItems: updatedQuote.completedSaleApprovalItems,
            })
          : quote,
      ),
    );
  }

  onAttachmentDeleted(attachmentId: string): void {
    this.attachments.update((current) => current.filter((a) => a.id !== attachmentId));
  }

  onAttachmentAdded(): void {
    const oppId = this.opportunityId();
    if (oppId) {
      this.loadOpportunityAttachments(oppId);
    }
  }

  onAttachmentUpdated(updatedAttachment: AttachmentInfo): void {
    this.attachments.update((current) =>
      current.map((attachment) =>
        attachment.id === updatedAttachment.id ? updatedAttachment : attachment,
      ),
    );
  }

  onDispositionUpdated(): void {
    const oppId = this.opportunityId();
    if (oppId) {
      this.loadOpportunity(oppId);
    }
  }

  onDeselect = (): void => {
    window.close();
  };
}
